// The audit-trail wire shapes — TWO here, THREE on the surface, and the split is
// the finding.
//
// `GET /v1/audit/events` (the dashboard feed) and the MCP `audit tail` action
// (the operator's at-a-glance) both answer "what has the fleet been doing", and
// both had a private projection. They are NOT the same body and never were:
//
// | Field | REST feed | MCP tail |
// |---|---|---|
// | `repo_full_name` | yes | no |
// | `verb` / `outcome` | yes (`integration.used` only) | no |
// | `mode` | no | yes |
// | the timestamp | `created_at_iso` | `ts_iso` |
//
// One door renders a repo-scoped activity feed with a usage panel; the other
// hands an agent a compact index it drills into. Merging them would have to add
// three fields to one and rename a timestamp on the other, and a renamed
// timestamp is a break for every client already reading it. So both are stated
// here, beside each other, where the difference is visible — rather than in two
// files that had no way to know about one another.
//
// ─── THE THIRD SHAPE, AND WHY IT IS NOT HERE (measured 2026-09-08) ────────────
//
// A reader of this module would conclude the surface has two audit bodies. It
// has three. `toAuditDto` (`apps/api/src/routes/admin.ts`) serves the CROSS-TENANT
// platform tail behind `GET /v1/admin/audit` and the MCP `account platform_audit`
// action, and it is a fourth field set again: `account_login` and `outcome`,
// which neither shape above carries, no `repo_full_name`, no `mode`, and the
// `ts_iso` spelling rather than `created_at_iso`.
//
// It is deliberately NOT promoted to a resource here, and the reason is the house
// rule against abstraction without a consumer: it already has exactly ONE
// implementation and ONE call site (`platformAuditPage`, `admin.ts`), which BOTH
// transports go through — the export is the mechanism, so there is no second copy
// for a resource to collapse. Promoting it would move code without deleting a
// duplicate. What it would buy is an OpenAPI body for that operation, which is
// slice 05's step 6 (publish the success bodies) and wants doing as that sweep
// rather than one row at a time.
//
// It is named here so this module's table is not read as the whole surface. If
// `toAuditDto` ever grows a second copy, THAT is when it moves in beside these
// two.
//
// Neither carries a payload. `input`/`output` are on the entity and stay there:
// the full record is the session's own event stream, and a feed that dumped
// them would put whatever a tool was handed onto an index screen.

import { z } from 'zod';
import { AuditModeSchema } from '../entities/audit';
import { defineResource } from './resource';

/** The row the REST feed reads — the tail row plus its repo and raw payloads. */
export type AuditFeedEntity = {
  id: string;
  sessionId: string;
  /** The session's repo, or null for a repo-less (gateway) session. */
  repoFullName: string | null;
  kind: string;
  tool: string | null;
  ts: Date;
  /** Raw payloads. Read for exactly two scalars, on exactly one kind. */
  input: Record<string, unknown> | null;
  output: Record<string, unknown> | null;
};

/** The row the MCP tail reads. */
export type AuditTailEntity = {
  id: string;
  sessionId: string;
  ts: Date;
  kind: string;
  tool: string | null;
  mode: z.infer<typeof AuditModeSchema>;
};

/**
 * The kind whose `verb`/`outcome` the feed unpacks.
 *
 * A literal rather than an import of `AuditEventKind.IntegrationUsed`: this
 * module's only need is the string, and the carve-out is deliberately ONE kind
 * — a second one earns a second line here, in view of the first.
 */
const INTEGRATION_USED = 'integration.used';

/** A payload scalar, or null. Empty string is not a value a client can render. */
function scalar(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

/** The REST feed body. Field names and nullability are the existing contract. */
export const AuditFeedWireSchema = z.object({
  id: z.string(),
  session_id: z.string(),
  repo_full_name: z.string().nullable(),
  kind: z.string(),
  /** The tool a call named; null on every non-tool-call kind. */
  tool: z.string().nullable(),
  /**
   * The integration verb, on `integration.used` rows only (#1222). Null on
   * every other kind — without it the connections usage panel renders the same
   * literal on every row and says nothing about who called what.
   */
  verb: z.string().nullable(),
  /** The integration call's outcome, on `integration.used` rows only. Null otherwise. */
  outcome: z.string().nullable(),
  created_at_iso: z.string(),
});

/** What a client should type against for a REST audit-feed row. */
export type AuditFeedWire = z.infer<typeof AuditFeedWireSchema>;

/** The MCP tail body — a compact index, deliberately narrower than the feed. */
export const AuditTailWireSchema = z.object({
  id: z.string(),
  session_id: z.string(),
  ts_iso: z.string(),
  kind: z.string(),
  tool: z.string().nullable(),
  /**
   * `live` or `replay`. Carried on this door and nowhere else on this surface —
   * `AuditModeSchema` rather than a bare string, so a mode added to the domain
   * reaches the published shape instead of widening it to anything.
   */
  mode: AuditModeSchema,
});

/** What a caller should type against for an MCP audit-tail row. */
export type AuditTailWire = z.infer<typeof AuditTailWireSchema>;

export const AuditFeedResource = defineResource<AuditFeedEntity, typeof AuditFeedWireSchema>({
  schema: AuditFeedWireSchema,
  serialize: (e) => {
    const usage = e.kind === INTEGRATION_USED;
    return {
      id: e.id,
      session_id: e.sessionId,
      repo_full_name: e.repoFullName,
      kind: e.kind,
      tool: e.tool,
      verb: usage ? scalar(e.input?.['verb']) : null,
      outcome: usage ? scalar(e.output?.['outcome']) : null,
      created_at_iso: e.ts.toISOString(),
    };
  },
});

export const AuditTailResource = defineResource<AuditTailEntity, typeof AuditTailWireSchema>({
  schema: AuditTailWireSchema,
  serialize: (e) => ({
    id: e.id,
    session_id: e.sessionId,
    ts_iso: e.ts.toISOString(),
    kind: e.kind,
    tool: e.tool,
    mode: e.mode,
  }),
});
